import { type SortingLogic } from '../enums/sortingLogic';
import { type PaginatedBackendResponse } from '../models/GhostPostModel';
import { type PlotlyResponse } from '../models/PlotlyModel';
import { type TPandasTable } from '../models/TPandasTable';
import { type MSingleStockCardSummary } from '../models/singleStock/MSingleStockCardSummary';
import type {
  CompanyInfo,
  CompanyListResponse,
  CompanyPerformanceResponse,
  StockReportModel
} from '../models/singleStock/SingleStockOverview';
import { type MSingleStockSummarizedInfo } from '../models/singleStock/details/MSingleStockSummarizedInfo';
import ApiService from './apiService';
import { EDjangoAppsEndpoints } from '../enums/EDjangoAppsEndpoints';

export default class SvSingleStockServices extends ApiService {
  static async listSingleStocks(page?: number, search?: string, sortBy?: SortingLogic) {
    return await super.get<PaginatedBackendResponse<MSingleStockCardSummary[]>>(EDjangoAppsEndpoints.singleStock, 'list', {
      page: page ?? 0,
      search: search ?? '',
      sortBy
    });
  }

  static async getSingleStockReportInfo(identifier?: string) {
    return await super.get<MSingleStockSummarizedInfo>(EDjangoAppsEndpoints.singleStock, `info/${identifier}`);
  }

  static async getSingleStockReportMarketCap(identifier?: string) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.singleStock, `market-cap/${identifier}`);
  }

  static async getSingleStockReportPerformanceDaily(param?: {
    companyId: string;
    startDate?: string;
    endDate?: string;
  }) {
    return await super.get<{ table: TPandasTable; chart: PlotlyResponse; startdate: string; enddate: string }>(
      EDjangoAppsEndpoints.singleStock,
      'performance/daily',
      param
    );
  }

  static async getSingleStockReportPerformanceMonthly(param?: { companyId: string; year?: string; }) {
    return await super.get<{ table: TPandasTable; chart: PlotlyResponse }>(EDjangoAppsEndpoints.singleStock, 'performance/monthly', param);
  }

  static async getSingleStockReportPerformanceYearly(param?: { companyId: string; year?: string; }) {
    return await super.get<{ table: TPandasTable; chart: PlotlyResponse; bars: PlotlyResponse }>(
      EDjangoAppsEndpoints.singleStock,
      'performance/yearly',
      param
    );
  }

  static async getSingleStockReportVolatility(param?: { companyId: string; }) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.singleStock, 'volatility', param);
  }

  static async getSingleStockReportDrawdown(param?: { companyId: string; }) {
    return await super.get<{ table: TPandasTable; chart: PlotlyResponse }>(EDjangoAppsEndpoints.singleStock, 'drawdown', param);
  }

  static async getSingleStockReportDividends(param?: { companyId: string; }) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.singleStock, 'dividends', param);
  }

  static async getSingleStockReportTradingVolumeToMarketCap(param?: { companyId: string; }) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.singleStock, 'trading-volume/market-cap', param);
  }

  static async getSingleStockReportRelativeTradingVolume(param?: { companyId: string; }) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.singleStock, 'trading-volume/relative', param);
  }

  /**
   * @deprecated
   * @param page
   * @param search
   */
  static async getAllStocks(page: number, search: string) {
    return (await super.get<CompanyListResponse>(EDjangoAppsEndpoints.stock, 'list-all', { page, search })).getData();
  }

  /**
   * @deprecated
   * @param cid
   * @param tid
   */
  static async getStockReport(cid: number, tid: number) {
    return (await super.get<StockReportModel>(EDjangoAppsEndpoints.singleStock, undefined, { cid, tid })).getData();
  }

  /**
   * @deprecated
   * @param companyId
   * @param tradingId
   * @param start
   * @param end
   */
  static async getStockPerformance(companyId: string, tradingId: string, start: string, end: string) {
    return (
      await super.get<CompanyPerformanceResponse>(EDjangoAppsEndpoints.stock, 'performance', {
        companyId,
        tradingId,
        start,
        end
      })
    ).getData();
  }

  /**
   * @deprecated
   * @param identifier
   */
  static async getCompanyInfo(identifier: string) {
    return (await super.get<CompanyInfo>(EDjangoAppsEndpoints.singleStock, `info/${identifier}`)).getData();
  }
}
